import Pusher, {Channel} from 'pusher-js'

type PusherHandler = (data?: unknown) => void

type PusherChannel = Pick<Channel, 'bind' | 'unbind'>

/**
 * In-page stand-in used when no app key is configured (E2E/CI), so nothing dials Pusher.
 * `window.__dispatchLocalPusher` delivers an event to the handlers bound on that channel.
 */
class LocalPusherChannel {
    private handlers = new Map<string, Set<PusherHandler>>()

    bind(event: string, handler: PusherHandler): this {
        const handlers = this.handlers.get(event) ?? new Set()
        handlers.add(handler)
        this.handlers.set(event, handlers)

        return this
    }

    unbind(event: string, handler?: PusherHandler): this {
        if (handler === undefined) {
            this.handlers.delete(event)

            return this
        }

        this.handlers.get(event)?.delete(handler)

        return this
    }

    emit(event: string, data?: unknown): void {
        this.handlers.get(event)?.forEach(handler => {
            handler(data)
        })
    }
}

const localChannels = new Map<string, LocalPusherChannel>()

function localChannel(name: string): LocalPusherChannel {
    const existing = localChannels.get(name)

    if (existing) {
        return existing
    }

    const channel = new LocalPusherChannel()
    localChannels.set(name, channel)

    return channel
}

function installLocalDispatch(): void {
    window.__dispatchLocalPusher = (channelName: string, event: string, data?: unknown): void => {
        localChannels.get(channelName)?.emit(event, data)
    }
}

class PusherService {
    private static instance: PusherService | null = null
    private client: Pusher | null = null

    private constructor() {}

    public static getInstance(): PusherService {
        if (!PusherService.instance) {
            localChannels.clear()
            PusherService.instance = new PusherService()
        }

        return PusherService.instance
    }

    private getClient(): Pusher | null {
        if (!window.pusherAppKey) {
            return null
        }

        if (!this.client) {
            this.client = new Pusher(window.pusherAppKey, {
                cluster: window.pusherAppCluster,
            })
        }
        return this.client
    }

    public subscribe(channelName: string): PusherChannel {
        const client = this.getClient()

        if (client) {
            return client.subscribe(channelName)
        }

        installLocalDispatch()

        return localChannel(channelName) as unknown as PusherChannel
    }

    public unsubscribe(channelName: string): void {
        if (this.client) {
            this.client.unsubscribe(channelName)

            return
        }

        localChannels.delete(channelName)
    }

    public disconnect(): void {
        if (this.client) {
            this.client.disconnect()
            this.client = null
        }

        localChannels.clear()
    }
}

export default PusherService
